import { __ } from "@wordpress/i18n";
/**
 * Modal Component
 *
 * Reusable modal/dialog rendered via portal. Used by spend-rules-list
 * (Redeem reward modal) and coupons-list (Reward terms modal).
 */
import React, { type FC, type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./modal.scss";

const FOCUSABLE_SELECTOR =
	'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: ReactNode;
	children: ReactNode;
	className?: string;
	closeAriaLabel?: string;
}

export const Modal: FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	children,
	className = "",
	closeAriaLabel,
}) => {
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const previouslyFocused = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!isOpen) return;

		previouslyFocused.current = document.activeElement as HTMLElement | null;
		const { body } = document;
		const htmlEl = document.documentElement;
		const previousBodyOverflow = body.style.overflow;
		const previousHtmlOverflow = htmlEl.style.overflow;
		body.style.overflow = "hidden";
		htmlEl.style.overflow = "hidden";

		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key !== "Tab") return;

			const root = dialogRef.current;
			if (!root) return;

			const focusable = Array.from(
				root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
			).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);

			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", handleKey);

		dialogRef.current?.focus();

		return () => {
			document.removeEventListener("keydown", handleKey);
			body.style.overflow = previousBodyOverflow;
			htmlEl.style.overflow = previousHtmlOverflow;
			previouslyFocused.current?.focus?.();
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const dialog = (
		<div
			className="leat-modal__overlay"
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					onClose();
				}
			}}
			role="presentation"
		>
			<div
				ref={dialogRef}
				className={`leat-modal ${className}`.trim()}
				role="dialog"
				aria-modal="true"
				aria-label={typeof title === "string" ? title : undefined}
				tabIndex={-1}
			>
				{title && (
					<div className="leat-modal__header">
						<h2 className="leat-modal__title">{title}</h2>
						<button
							type="button"
							className="leat-modal__close"
							onClick={onClose}
							aria-label={closeAriaLabel ?? __("Close", "leat-crm")}
						>
							<svg
								width="17"
								height="17"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<line x1="18" y1="6" x2="6" y2="18" />
								<line x1="6" y1="6" x2="18" y2="18" />
							</svg>
						</button>
					</div>
				)}
				<div className="leat-modal__body">{children}</div>
			</div>
		</div>
	);

	return createPortal(dialog, document.body);
};

export default Modal;
